import api from "../../../utils/api";

export interface EventoEnvio {
  id: string;
  titulo: string;
  descripcion: string;
  fecha: string;
  ubicacion: string;
  esActual: boolean;
}

export interface Envio {
  id: string;
  guia: string;
  empresa: string;
  shipment_name: string;
  origen: string;
  destino: string;
  ubicacionOrigen: { ciudad: string; estado: string };
  ubicacionDestino: { ciudad: string; estado: string };
  estado: "pendiente" | "en-transito" | "entregado" | "incidencia";
  ultimaActualizacion: string;
  eta: string;
  badges: string[];
  puedeCopiar: boolean;
  estadoColor: "green" | "blue" | "orange" | "red";
  eventos?: EventoEnvio[];
}

export interface EnviosResponse {
  envios: Envio[];
  total: number;
}

export interface EnviosSettings {
  appearance?: {
    headerTitle?: string;
    headerSubtitle?: string;
  };
}

interface BackendShipment {
  id?: string;
  client_id: string;
  vehicle_id: string;
  client_name: string;
  shipment_name: string;
  origin: { lat?: number; long?: number; address?: string; ciudad?: string; estado?: string };
  destination: { lat?: number; long?: number; address?: string; ciudad?: string; estado?: string };
  tracking_number: string;
  transportation_type: string;
  status_id: string;
  note?: string;
  event_history?: any[];
  created_at?: any;
  updated_at?: any;
}

interface BackendResponse {
  data: BackendShipment[];
  meta?: { total?: number; page?: number; limit?: number };
  page?: number;
  total?: number;
  limit?: number;
}

const statusMap: Record<string, "pendiente" | "en-transito" | "entregado" | "incidencia"> = {
  pendiente: "pendiente",
  en_transito: "en-transito",
  entregado: "entregado",
  incidencia: "incidencia",
};

const statusColorMap: Record<string, "green" | "blue" | "orange" | "red"> = {
  pendiente: "orange",
  "en-transito": "blue",
  entregado: "green",
  incidencia: "red",
};

function transformBackendShipment(shipment: BackendShipment): Envio {
  const estado = statusMap[shipment.status_id] || "pendiente";
  const originAddress = shipment.origin?.address || shipment.origin?.ciudad || "";
  const destinationAddress = shipment.destination?.address || shipment.destination?.ciudad || "";

  return {
    id: shipment.id || shipment.tracking_number,
    guia: shipment.tracking_number,
    empresa: shipment.client_name,
    shipment_name: shipment.shipment_name,
    origen: originAddress,
    destino: destinationAddress,
    ubicacionOrigen: {
      ciudad: shipment.origin?.address || shipment.origin?.ciudad || "",
      estado: shipment.origin?.estado || "",
    },
    ubicacionDestino: {
      ciudad: shipment.destination?.address || shipment.destination?.ciudad || "",
      estado: shipment.destination?.estado || "",
    },
    estado,
    ultimaActualizacion: "Reciente",
    eta: "Por confirmar",
    badges: [shipment.transportation_type],
    puedeCopiar: true,
    estadoColor: statusColorMap[estado],
    eventos: shipment.event_history?.map((evt: any) => ({
      id: evt.id || Date.now().toString(),
      titulo: evt.titulo || evt.title || evt.event_type || "",
      descripcion: evt.descripcion || evt.description || evt.address || "",
      fecha: evt.fecha || evt.date || "",
      ubicacion: evt.ubicacion || evt.location || evt.address || "",
      esActual: evt.esActual || evt.isCurrent || false,
    })) || [],
  };
}

export async function getEnvios(filters?: Record<string, any>): Promise<EnviosResponse> {
  try {
    const params = {
      limit: filters?.limit || 20,
      page: filters?.page || 1,
      ...filters,
    };
    const response = await api.get<BackendResponse>("/shipmentList", { params });
    const data = response.data.data || [];

    return {
      envios: data.map(transformBackendShipment),
      total: response.data.meta?.total || response.data.total || data.length,
    };
  } catch (error) {
    console.error("Error fetching envios:", error);
    throw error;
  }
}

export async function getEnvio(id: string): Promise<Envio> {
  try {
    const response = await api.get<BackendShipment>("/shipment", { params: { id } });
    return transformBackendShipment(response.data);
  } catch (error) {
    console.error("Error fetching envio:", error);
    throw error;
  }
}

export async function updateEnvio(id: string, data: Partial<Envio>): Promise<Envio> {
  try {
    const updateData = {
      shipment_name: data.empresa,
      origin: { ciudad: data.ubicacionOrigen?.ciudad },
      destination: { ciudad: data.ubicacionDestino?.ciudad },
      note: data.badges?.join(","),
    };
    const response = await api.put<BackendShipment>("/shipment", updateData, {
      params: { id },
    });
    return transformBackendShipment(response.data);
  } catch (error) {
    console.error("Error updating envio:", error);
    throw error;
  }
}

export async function getSettings(): Promise<EnviosSettings> {
  try {
    const response = await api.get<EnviosSettings>("/envios/settings");
    return response.data;
  } catch {
    return {};
  }
}
